import React, { useEffect } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useActionHook } from "hooks";
import { useDealActions } from "store";
import { Characteristic } from "store/types";
import { SampleDeal } from "store/types/user";

import { InvestmentData } from "./InvestmentStep";

interface InvestmentCardProps {
  investment: InvestmentData | SampleDeal;
  sample?: boolean;
  characteristicsList: Characteristic[];
}

function InvestmentCard({ investment, sample = false, characteristicsList }: InvestmentCardProps) {
  const dealName = "dealName" in investment ? investment.dealName : investment.title;
  const assetClassName =
    "assetClass" in investment && investment.assetClass
      ? typeof investment.assetClass === "string"
        ? investment.assetClass
        : investment.assetClass.account_alias || investment.assetClass.name
      : "asset_class" in investment
        ? investment.asset_class
        : "";

  const subAssetClassName =
    "subAssetClass" in investment && investment.subAssetClass
      ? investment.subAssetClass.account_alias || investment.subAssetClass.name
      : "";

  const geography = "geography" in investment ? investment.geography : "";
  const vintage = "vintage" in investment ? investment.vintage : null;
  const commitment = "commitment" in investment ? investment.commitment : null;

  // Get characteristics from extractedData for InvestmentData, or use vintage/commitment for SampleDeal
  const characteristics =
    "extractedData" in investment && investment.extractedData?.characteristics
      ? investment.extractedData.characteristics
      : [];

  return (
    <Box
      sx={{
        border: "1.5px solid rgba(10, 49, 66, 0.12)",
        borderRadius: "10px",
        p: 2.5,
        bgcolor: sample ? "rgba(10, 49, 66, 0.02)" : "#fff",
        opacity: sample ? 0.7 : 1
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography sx={{ fontSize: "15px", fontWeight: 500, color: "#0a3142" }}>{dealName}</Typography>
        {assetClassName && (
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              bgcolor: "#e8f6f7",
              borderRadius: "50px",
              fontSize: "12px",
              fontWeight: 500,
              color: "#26a6ae"
            }}
          >
            {assetClassName}
          </Box>
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        {subAssetClassName && (
          <Box>
            <Typography sx={{ fontSize: "12px", color: "rgba(10, 49, 66, 0.5)", mb: 0.5 }}>Sub Asset Class</Typography>
            <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#0a3142" }}>{subAssetClassName}</Typography>
          </Box>
        )}
        <Box>
          <Typography sx={{ fontSize: "12px", color: "rgba(10, 49, 66, 0.5)", mb: 0.5 }}>Currency</Typography>
          <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#0a3142" }}>
            {"currency" in investment ? investment.currency : "USD"}
          </Typography>
        </Box>
        {geography && (
          <Box>
            <Typography sx={{ fontSize: "12px", color: "rgba(10, 49, 66, 0.5)", mb: 0.5 }}>Geography</Typography>
            <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#0a3142" }}>{geography}</Typography>
          </Box>
        )}
        {vintage && (
          <Box>
            <Typography sx={{ fontSize: "12px", color: "rgba(10, 49, 66, 0.5)", mb: 0.5 }}>Vintage</Typography>
            <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#0a3142" }}>{vintage}</Typography>
          </Box>
        )}
        {commitment !== null && commitment > 0 && (
          <Box>
            <Typography sx={{ fontSize: "12px", color: "rgba(10, 49, 66, 0.5)", mb: 0.5 }}>Commitment</Typography>
            <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#0a3142" }}>
              {new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "currency" in investment && investment.currency ? investment.currency : "USD"
              }).format(commitment)}
            </Typography>
          </Box>
        )}
        {/* Display characteristics */}
        {characteristics
          .filter((c) => c.value && c.value.trim())
          .map((char, idx) => {
            const charDef = characteristicsList.find((c) => c.id === char.characteristic_id);
            const charName = char.name || charDef?.name || "Unknown";

            return (
              <Box key={idx}>
                <Typography sx={{ fontSize: "12px", color: "rgba(10, 49, 66, 0.5)", mb: 0.5 }}>{charName}</Typography>
                <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#0a3142" }}>
                  {char.currency ? `${char.currency} ${char.value}` : char.value}
                </Typography>
              </Box>
            );
          })}
      </Box>
    </Box>
  );
}

interface ReadyStepProps {
  step: number;
  totalSteps: number;
  investments: InvestmentData[];
  skipped: boolean;
  sampleDeals: SampleDeal[];
  submitting?: boolean;
  onBack: () => void;
  onFinish: () => void;
  onAddAnother?: () => void;
}

export const ReadyStep: React.FC<ReadyStepProps> = ({
  step,
  totalSteps,
  investments,
  skipped,
  sampleDeals,
  submitting,
  onBack,
  onFinish,
  onAddAnother
}) => {
  const { getCharacteristicsGeneralDataAction } = useDealActions();
  const {
    data: generalCharacteristicsData = { characteristic_list: [], categories: [] },
    action: getGeneralCharacteristics
  } = useActionHook(getCharacteristicsGeneralDataAction);

  const characteristicsList = generalCharacteristicsData?.characteristic_list.filter(
    (c) => c.name !== "Geography" && c.name !== "Currency"
  );

  useEffect(() => {
    getGeneralCharacteristics();
  }, [getGeneralCharacteristics]);

  const useSample = !investments.length || skipped;
  const displayInvestments = useSample ? sampleDeals : investments;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        position: "relative"
      }}
    >
      {/* Back button - top left */}
      <Button
        onClick={onBack}
        startIcon={<ArrowBackIcon sx={{ fontSize: 14, color: "#26a6ae" }} />}
        sx={{
          position: "absolute",
          top: 24,
          left: 32,
          color: "#26a6ae",
          textTransform: "none",
          fontSize: "14px",
          minWidth: "auto",
          p: 0.5,
          "&:hover": {
            opacity: 0.8
          }
        }}
      >
        Back
      </Button>

      {/* Step Counter */}
      <Box
        sx={{
          position: "absolute",
          top: 24,
          right: 32,
          fontSize: "13px",
          color: "rgba(10, 49, 66, 0.5)",
          fontWeight: 500
        }}
      >
        Step {step} of {totalSteps}
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: 3
        }}
      >
        <Box sx={{ maxWidth: "640px", width: "100%", textAlign: "center" }}>
          {/* Checkmark */}
          <Box
            sx={{
              display: "inline-flex",
              mb: 3
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 56, color: "#26a6ae" }} />
          </Box>

          <Typography
            variant="h4"
            sx={{
              fontSize: "28px",
              fontWeight: 500,
              color: "#0a3142",
              mb: 1
            }}
          >
            You&apos;re ready
          </Typography>

          <Typography
            sx={{
              fontSize: "15px",
              color: "rgba(10, 49, 66, 0.6)",
              mb: 3
            }}
          >
            {useSample
              ? "We've loaded sample data so you can explore the platform right away."
              : investments.length === 1
                ? "Your workspace is set up. Here's your investment."
                : `Your workspace is set up. Here are your ${investments.length} investments.`}
          </Typography>

          {useSample && (
            <Box
              sx={{
                bgcolor: "#e8f6f7",
                borderRadius: "8px",
                p: 1.5,
                mb: 3,
                fontSize: "13px",
                color: "#26a6ae"
              }}
            >
              This is sample data — add your first real investment anytime.
            </Box>
          )}

          {/* Investment Cards */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 4, textAlign: "left" }}>
            {displayInvestments.map((inv, i) => (
              <InvestmentCard key={i} investment={inv} sample={useSample} characteristicsList={characteristicsList} />
            ))}
          </Box>

          {/* Actions */}
          <Stack direction="column" gap="12px" alignItems="center" justifyContent="center">
            <Stack direction="row" justifyContent="center" gap="12px">
              {!useSample && investments.length < 3 && onAddAnother && (
                <Button
                  variant="outlined"
                  onClick={onAddAnother}
                  sx={{
                    borderColor: "rgba(10, 49, 66, 0.2)",
                    color: "#0a3142",
                    fontSize: "15px",
                    fontWeight: 500,
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "#c09560",
                      bgcolor: "rgba(192, 149, 96, 0.02)"
                    }
                  }}
                >
                  Add another investment
                </Button>
              )}
              <Button
                variant="contained"
                onClick={onFinish}
                disabled={submitting}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  bgcolor: "#071f35",
                  boxShadow: "none",
                  color: "#fff",
                  fontSize: "15px",
                  fontWeight: 500,
                  textTransform: "none",
                  "&:hover": {
                    bgcolor: "#0f2d47"
                  }
                }}
              >
                Go to my portfolio
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};
